import { useEffect } from 'react';

// Stands in for the real gpt.js when `mock` is on (local/mocked dev, `MOCK=true`): a real GPT request
// from `localhost` gets "no fill" from Google's ad exchange (no line items target that domain), so the
// slot div collapses to 0x0 — there's nothing to visually verify against. This fake implements only the
// slice of the GPT API GoogleAdUnit.tsx calls (cmd/defineSlot/destroySlots/pubads/enableServices/
// display) and, on
// `display()`, appends a sized, labelled placeholder as a CHILD of the target div rather than restyling
// the div itself — real GPT fills a slot by inserting a creative iframe inside the existing div, it never
// touches the div's own layout. `inject`-type slots (e.g. ad-left/ad-right skyscrapers) already have
// real CSS-driven sizing/positioning (sticky rails) from the surrounding layout; overwriting the div's
// own display/width/height, as an earlier version of this mock did, clobbered that and broke the page.
const MOCK_GOOGLETAG_SCRIPT = `
  (function () {
    var slotsByTargetNode = {};
    var slotRenderEndedListeners = [];
    window.googletag = {
      cmd: { push: function (fn) { fn(); } },
      defineSlot: function (adUnitCode, dimensions, targetNode) {
        var slot = {
          addService: function () { return slot; },
          setTargeting: function () { return slot; },
          getSlotElementId: function () { return targetNode; },
        };
        slotsByTargetNode[targetNode] = { dimensions: dimensions, slot: slot };
        return slot;
      },
      destroySlots: function () {},
      pubads: function () {
        return {
          enableSingleRequest: function () {},
          addEventListener: function (eventName, listener) {
            if (eventName === 'slotRenderEnded') slotRenderEndedListeners.push(listener);
          },
        };
      },
      enableServices: function () {},
      display: function (targetNode) {
        var entry = slotsByTargetNode[targetNode];
        var el = document.getElementById(targetNode);
        if (!entry || !el) return;
        var pair = Array.isArray(entry.dimensions[0]) ? entry.dimensions[0] : entry.dimensions;
        var width = pair[0];
        var height = pair[1];
        var creative = document.createElement('div');
        creative.style.display = 'flex';
        creative.style.alignItems = 'center';
        creative.style.justifyContent = 'center';
        creative.style.width = width + 'px';
        creative.style.height = height + 'px';
        creative.style.maxWidth = '100%';
        creative.style.margin = '0 auto';
        creative.style.background = '#e0e0e0';
        creative.style.border = '1px dashed #999';
        creative.style.boxSizing = 'border-box';
        creative.style.color = '#666';
        creative.style.fontSize = '12px';
        creative.textContent = targetNode + ': ' + width + 'x' + height;
        el.appendChild(creative);
        // Mock always "fills", so every listener sees a non-empty render — matching what a real
        // creative delivery would fire slotRenderEnded with.
        slotRenderEndedListeners.forEach(function (listener) {
          listener({ isEmpty: false, slot: entry.slot });
        });
      },
    };
  })();
`;

export const GooglePublisherTags = ({
  mock = false,
  shouldRenderPostHydration = false,
}: {
  mock?: boolean;
  shouldRenderPostHydration?: boolean;
}) => {
  useEffect(() => {
    if (mock || !shouldRenderPostHydration) return;

    const timer = setTimeout(() => {
      const script = document.createElement('script');
      script.async = true;
      script.src = 'https://securepubads.g.doubleclick.net/tag/js/gpt.js';
      document.head.appendChild(script);
    }, 100);

    return () => clearTimeout(timer);
  }, [mock, shouldRenderPostHydration]);

  if (mock) {
    return <script dangerouslySetInnerHTML={{ __html: MOCK_GOOGLETAG_SCRIPT }} />;
  }

  if (shouldRenderPostHydration) {
    return null;
  }

  return <script async src="https://securepubads.g.doubleclick.net/tag/js/gpt.js" />;
};
